/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { addDays, addMinutes, addWeeks, format, parse, startOfWeek } from 'date-fns';
import { INDUSTRY_META as BUSINESS_TYPE_META } from '../types/consulting';
import { consultingApi, orchestratorApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { loadBusinessContext, saveBusinessContext } from '../utils/businessContext';
import {
  normalizeBusinessType,
  resolveBusinessTypeFromStackId,
  resolveStackIdFromBusinessType,
  BUSINESS_TYPE_BENCHMARKS,
  BUSINESS_TYPES,
} from '../utils/businessTaxonomy';
import { isReadinessCompletedStored, loadReadinessProfile, saveReadinessProfile } from '../utils/readinessStorage';

const TABS = [
  { id: 'packages', label: 'Packages & Pricing' },
  { id: 'roi', label: 'Readiness & ROI' },
  { id: 'my-flow', label: 'My AI Flow' },
  { id: 'start', label: 'Get Started (Free)' },
];

const PACKAGES = [
  {
    key: 'assessment',
    title: 'AI Readiness Assessment',
    phase: 'Phase 1',
    timeline: '1-2 weeks',
    base: 2500,
    minQuote: 2500,
    maxQuote: 8000,
    priceLabel: '$2,500 - $8,000',
    bestFor: "Businesses that know they need AI but don't know where to start",
    accent: '#FFE800',
    deliverables: [
      'Business process audit',
      'Data & systems review',
      'AI opportunity map',
      'Tool landscape evaluation (30+ tools)',
      'Implementation roadmap',
      'Executive summary report',
    ],
    scopeDrivers: [
      'Business size and number of departments audited',
      'Data complexity and compliance overlay',
      'Executive report depth and travel requirements',
    ],
  },
  {
    key: 'pilot',
    title: 'Pilot Launch',
    phase: 'Phase 2',
    timeline: '4-6 weeks',
    base: 3500,
    minQuote: 3500,
    maxQuote: 12000,
    priceLabel: '$3,500 - $12,000',
    bestFor: 'Businesses wanting proof before committing to full rollout',
    accent: '#FF6B35',
    featured: true,
    deliverables: [
      '30-day scoped implementation',
      'Tool selection + integration',
      'Team training (up to 5 staff)',
      'Before/after performance dashboard',
      'ROI report',
      'Expansion roadmap',
    ],
    scopeDrivers: [
      'Automation and workflow complexity',
      'Integration count and customization depth',
      'Training scope and accelerated timeline needs',
    ],
  },
  {
    key: 'fullstack',
    title: 'Full Stack Implementation',
    phase: 'Phase 3',
    timeline: '8-16 weeks',
    base: 8000,
    minQuote: 8000,
    maxQuote: 25000,
    priceLabel: '$8,000 - $25,000',
    bestFor: 'Businesses ready for comprehensive AI transformation',
    accent: '#00D4FF',
    deliverables: [
      'Multi-process automation suite',
      'Full tool stack integration',
      'Full team training + SOPs',
      'Custom documentation',
      '90-day monitoring',
      'Three optimization reviews',
    ],
    scopeDrivers: [
      'Number of processes and automations in scope',
      'Complex integrations and documentation breadth',
      'Large-team onboarding and change management load',
    ],
  },
  {
    key: 'retainer',
    title: 'Ongoing Retainer',
    phase: 'Phase 4',
    timeline: 'Monthly',
    base: 1500,
    minQuote: 1500,
    maxQuote: 5000,
    priceLabel: '$1,500 - $5,000/mo',
    bestFor: 'Post-implementation businesses wanting to stay optimized',
    accent: '#39FF14',
    deliverables: [
      'Monthly performance review',
      'Tool updates + new integrations',
      'Strategy calls',
      'Refresher training',
      'Competitive monitoring',
      'Priority access',
    ],
    scopeDrivers: [
      'Active tools maintained and support volume',
      'Reporting depth and optimization cadence',
      'Priority SLA expectations and response windows',
    ],
  },
];

const BUDGET_OPTIONS = [
  { value: 'under5k', label: 'Under $5k' },
  { value: '5k-15k', label: '$5k-$15k' },
  { value: '15k-30k', label: '$15k-$30k' },
  { value: '30kplus', label: '$30k+' },
  { value: 'unsure', label: 'Not sure yet' },
];

const SIZE_OPTIONS = [
  { value: 'small', label: '1-10' },
  { value: 'medium', label: '11-50' },
  { value: 'large', label: '51-200' },
];



const ECOSYSTEM_OPTIONS = [
  { value: 'microsoft', label: 'Microsoft 365', weight: 1.0 },
  { value: 'google', label: 'Google Workspace', weight: 1.2 },
  { value: 'legacy', label: 'Legacy / Manual', weight: 1.5 },
];

const EMPLOYEE_BANDS = [
  { value: 'lt2', label: '<2', min: 1, max: 1, representative: 1, multiplier: 0.8 },
  { value: '2-5', label: '2-5', min: 2, max: 5, representative: 4, multiplier: 1.0 },
  { value: '5-10', label: '5-10', min: 5, max: 10, representative: 8, multiplier: 1.2 },
  { value: '10-50', label: '10-50', min: 10, max: 50, representative: 25, multiplier: 1.5 },
  { value: '50-200', label: '50-200', min: 50, max: 200, representative: 100, multiplier: 2.0 },
  { value: '200+', label: '200+', min: 200, max: 999999, representative: 220, multiplier: 2.4 },
];

const ROI_CONSTANTS = {
  basePilot: 9500,
  baseBuild: 30000,
  laborRate: 65,
  hoursSavedPerUser: 15,
  maintenanceMonthly: 1800,
};

const CONSULTATION_WINDOWS = [
  'Monday-Friday, 9:00 AM-12:00 PM',
  'Monday-Friday, 1:00 PM-5:00 PM',
  'Timezone: Your local timezone (confirmed via email)',
];

const SLOT_DURATION_MINUTES = 20;
const SLOT_GAP_MINUTES = 20;
const SLOT_INTERVAL_MINUTES = SLOT_DURATION_MINUTES + SLOT_GAP_MINUTES;
const WEEKDAY_INDEX = {
  sun: 0,
  sunday: 0,
  mon: 1,
  monday: 1,
  tue: 2,
  tues: 2,
  tuesday: 2,
  wed: 3,
  wednesday: 3,
  thu: 4,
  thur: 4,
  thurs: 4,
  thursday: 4,
  fri: 5,
  friday: 5,
  sat: 6,
  saturday: 6,
};

const MY_AI_FLOW_DRAFT_KEY = 'wowrolla_my_ai_flow_draft_v1';
const MY_AI_FLOW_PDF_PACKAGE_KEY = 'wowrolla_last_my_ai_flow_pdf_package_v1';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATA_CENTRALIZATION_OPTIONS = [
  { value: 'scattered', label: 'Scattered across inboxes, sheets, and chat' },
  { value: 'partial', label: 'Partly centralized with some manual handoffs' },
  { value: 'centralized', label: 'Mostly centralized in CRM, ERP, or one source of truth' },
];

const COMPLIANCE_OPTIONS = [
  { value: 'low', label: 'Low compliance pressure' },
  { value: 'medium', label: 'Moderate approvals or audit requirements' },
  { value: 'high', label: 'High compliance, privacy, or legal review needs' },
];

const URGENCY_OPTIONS = [
  { value: 'exploring', label: 'Exploring this quarter' },
  { value: 'quarter', label: 'Need a pilot this quarter' },
  { value: 'now', label: 'Need operating relief now' },
];

const CHANGE_READINESS_LABELS = {
  1: 'Skeptical team, no internal owner yet',
  2: 'Some curiosity, but adoption will be slow',
  3: 'One internal champion and room to pilot',
  4: 'Leadership support and available operators',
  5: 'Actively pushing for rollout and accountability',
};

const DATA_CENTRALIZATION_SCORE = {
  scattered: 1,
  partial: 3,
  centralized: 5,
};

const COMPLIANCE_SCORE = {
  low: 2,
  medium: 3,
  high: 5,
};

const URGENCY_SCORE = {
  exploring: 2,
  quarter: 3,
  now: 5,
};

const normalizeIndustryKey = (raw = '') => {
  return normalizeBusinessType(raw, '');
};

const resolvePlaybookKey = (businessType = '') => {
  const key = normalizeBusinessType(businessType, 'other');
  const mapping = {
    real_estate: 'realestate',
    legal: 'professional',
    accounting: 'professional',
    plumber: 'other',
    electrician: 'other',
    hvac: 'other',
    dental: 'healthcare',
    law_enforcement_supplier: 'other',
    immigration_consultant: 'professional',
    mortgage_broker: 'professional',
    other: 'other',
  };
  return mapping[key] || 'other';
};

const inferEmployeeBand = (teamSizeRaw) => {
  const teamSize = Number(teamSizeRaw);
  if (!Number.isFinite(teamSize) || teamSize < 1) return '2-5';
  const matched = EMPLOYEE_BANDS.find((band) => teamSize >= band.min && teamSize <= band.max);
  return matched?.value || '200+';
};

const getEmployeeBandMeta = (bandValue) => {
  return EMPLOYEE_BANDS.find((band) => band.value === bandValue) || EMPLOYEE_BANDS[1];
};

const getComplexityMultiplier = (systemsTouchCountRaw) => {
  const systemsTouchCount = Number(systemsTouchCountRaw) || 1;
  if (systemsTouchCount <= 2) return 1.0;
  if (systemsTouchCount <= 5) return 1.3;
  return 1.7;
};

const getEcosystemWeight = (ecosystem) => {
  return ECOSYSTEM_OPTIONS.find((item) => item.value === ecosystem)?.weight || 1.0;
};

const inferDataCentralization = (systemsTouchCountRaw) => {
  const systemsTouchCount = Number(systemsTouchCountRaw) || 1;
  if (systemsTouchCount <= 2) return 'centralized';
  if (systemsTouchCount <= 5) return 'partial';
  return 'scattered';
};

const parseCurrentStackTokens = (value = '') => {
  return String(value || '')
    .toLowerCase()
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const ECOSYSTEM_PLAYBOOK_BIAS = {
  microsoft: {
    'lead-response': 2,
    reporting: 2,
    proposal: 2,
    'follow-up-care': 2,
    handoffs: 2,
  },
  google: {
    'content-ops': 2,
    reporting: 2,
    knowledge: 2,
    'menu-social': 2,
    'customer-comms': 2,
  },
  legacy: {
    handoffs: 3,
    'lead-response': 2,
    intake: 2,
    'support-triage': 2,
    appointments: 2,
  },
};

const ROI_FIELD_HINTS = {
  businessType: 'Select the closest operating category for benchmark-level COE and workflow defaults.',
  teamSize: 'Employee band drives the size multiplier (Sm).',
  currentEcosystem: 'Current stack friction drives environment weight (We).',
  systemsTouchCount: 'How many apps one data object touches drives complexity (Cs).',
  toolCount: 'Count the active tools your team uses weekly.',
  currentToolStack: 'List current tools separated by commas so integration and seat scaling are more realistic.',
  dataCentralization: 'Data centralization is inferred from systems touch count for the new algorithm.',
  compliance: 'Approval intensity and governance constraints.',
  urgency: 'How soon you need measurable workflow relief.',
  changeReadiness: 'Leadership and team readiness to adopt new operating routines.',
  workflowPlaybook: 'Workflow recommendations adapt to your ecosystem and current tool stack; choose first-rollout workflows.',
};

const FieldHeader = ({ label, hint }) => (
  <div className="field-heading-row mb-2">
    <p className="field-heading m-0">{label}</p>
    {hint && (
      <span className="field-hint-dot" title={hint} aria-label={hint}>
        i
      </span>
    )}
  </div>
);

const PLAYBOOKS = {
  realestate: {
    summary: 'Prioritize speed-to-lead, listing production, and follow-up continuity before adding deeper forecasting or analytics.',
    workflows: [
      {
        id: 'lead-response',
        title: 'Lead capture and routing',
        impact: 'Stops hot leads from going cold after hours.',
        tools: ['Tidio', 'HubSpot', 'Make.com'],
        trend: 'Fast routing beats adding another generic chatbot widget.',
      },
      {
        id: 'listing-engine',
        title: 'Listing content and media ops',
        impact: 'Compresses listing prep time and keeps tone consistent.',
        tools: ['Claude.ai', 'Canva', 'Airtable'],
        trend: 'Specialized listing workflows outperform generic copy tools alone.',
      },
      {
        id: 'follow-up',
        title: 'Nurture and showing coordination',
        impact: 'Makes follow-up reliable instead of agent-memory driven.',
        tools: ['Calendly', 'HubSpot', 'Zapier'],
        trend: 'Automated handoffs matter more than adding more lead sources.',
      },
    ],
    marketWatch: [
      'Prefer workflow-connected CRM automation over one-off copy generators.',
      'Avoid paying for standalone transcription unless it drives an actual downstream workflow.',
    ],
  },
  marketing: {
    summary: 'Start with repeatable campaign workflows where speed, reporting, and client communication are visibly painful.',
    workflows: [
      {
        id: 'content-ops',
        title: 'Content production pipeline',
        impact: 'Cuts planning and drafting bottlenecks across client accounts.',
        tools: ['Claude.ai', 'Notion AI', 'Buffer'],
        trend: 'Agencies win with orchestration, not isolated writing tools.',
      },
      {
        id: 'reporting',
        title: 'Performance reporting and narrative',
        impact: 'Turns repetitive reporting into a reusable delivery system.',
        tools: ['Looker Studio', 'Supermetrics', 'Claude.ai'],
        trend: 'Clients increasingly value fast interpretation over raw dashboard access.',
      },
      {
        id: 'proposal-delivery',
        title: 'Proposal and pitch assembly',
        impact: 'Shortens sales cycles and reduces founder bottlenecks.',
        tools: ['HubSpot', 'Gamma', 'PandaDoc'],
        trend: 'Deck builders are becoming formatting layers, so keep the source content portable.',
      },
    ],
    marketWatch: [
      'Keep proposals and campaign logic in reusable systems, not trapped in one presentation tool.',
      'Use premium models for strategy and cheaper automation for tagging, routing, and summaries.',
    ],
  },
  ecommerce: {
    summary: 'Fix support, merchandising, and retention loops before expanding into broader AI personalization programs.',
    workflows: [
      {
        id: 'support-triage',
        title: 'Support triage and escalation',
        impact: 'Reduces response lag while keeping complex tickets human-owned.',
        tools: ['Gorgias', 'Shopify Inbox', 'Claude.ai'],
        trend: 'Blended human escalation is outperforming fully hands-off support bots.',
      },
      {
        id: 'merchandising',
        title: 'Product content and merchandising',
        impact: 'Speeds launches without sacrificing product specificity.',
        tools: ['Describely', 'Shopify', 'Canva'],
        trend: 'Vertical product-content tools are stronger than generic writers for SKU-heavy catalogs.',
      },
      {
        id: 'retention',
        title: 'Lifecycle retention campaigns',
        impact: 'Improves repeat purchase behavior with event-based follow-up.',
        tools: ['Klaviyo', 'Postscript', 'Make.com'],
        trend: 'Owned-channel automation is getting more efficient than paid reacquisition alone.',
      },
    ],
    marketWatch: [
      'Consolidate support tools before adding more AI layers if agents already work across too many tabs.',
      'Prioritize structured customer events because clean triggers create the best ROI.',
    ],
  },
  professional: {
    summary: 'For service firms, the fastest wins usually come from intake, proposal generation, and internal knowledge reuse.',
    workflows: [
      {
        id: 'intake',
        title: 'Client intake and qualification',
        impact: 'Stops founders from manually re-answering the same questions.',
        tools: ['Typeform', 'Calendly', 'Claude.ai'],
        trend: 'Structured intake beats open-ended email threads for AI readiness.',
      },
      {
        id: 'proposal',
        title: 'Proposal and scope drafting',
        impact: 'Creates faster turnaround with better package consistency.',
        tools: ['PandaDoc', 'Claude.ai', 'HubSpot'],
        trend: 'Proposal automation works best when tied to CRM stage and pricing logic.',
      },
      {
        id: 'knowledge',
        title: 'Knowledge capture and reuse',
        impact: 'Turns past delivery into repeatable internal IP.',
        tools: ['Notion', 'Fireflies', 'Zapier'],
        trend: 'Meeting capture alone is weak unless notes become searchable playbooks and templates.',
      },
    ],
    marketWatch: [
      'Do not buy transcription-only tools without a post-meeting execution workflow.',
      'Service firms get the best results when AI reduces proposal and delivery variance first.',
    ],
  },
  healthcare: {
    summary: 'Sequence work around no-show prevention, intake quality, and follow-up while respecting compliance overhead.',
    workflows: [
      {
        id: 'appointments',
        title: 'Appointment recovery and reminders',
        impact: 'Cuts no-shows and front-desk interruptions.',
        tools: ['Twilio', 'Calendly', 'Make.com'],
        trend: 'Operational messaging with clear guardrails is more valuable than broad patient-facing AI.',
      },
      {
        id: 'intake-risk',
        title: 'Intake and risk flagging',
        impact: 'Improves handoff quality before a provider sees the case.',
        tools: ['Typeform', 'Claude.ai', 'Airtable'],
        trend: 'Use AI to structure intake, not to replace clinical judgment.',
      },
      {
        id: 'follow-up-care',
        title: 'Follow-up and recall workflow',
        impact: 'Keeps patients from falling out of the care loop.',
        tools: ['HubSpot', 'Twilio', 'Zapier'],
        trend: 'The strongest healthcare ROI still comes from admin relief and compliance-aware follow-up.',
      },
    ],
    marketWatch: [
      'Bias toward human-in-the-loop automation when privacy or clinical context is sensitive.',
      'Document where AI stops and staff approval begins before scaling patient workflows.',
    ],
  },
  restaurant: {
    summary: 'Good hospitality implementations start with reservations, reviews, and social/menu production instead of flashy front-of-house bots.',
    workflows: [
      {
        id: 'reservation-ops',
        title: 'Reservation confirmations and no-show reduction',
        impact: 'Improves table utilization and lowers phone interruption volume.',
        tools: ['OpenTable', 'Twilio', 'Make.com'],
        trend: 'Simple reminder and fill-gap workflows beat expensive AI concierge experiments for SMB operators.',
      },
      {
        id: 'review-ops',
        title: 'Review response and reputation management',
        impact: 'Protects brand response time without consuming manager hours.',
        tools: ['Google Business Profile', 'Claude.ai', 'Zapier'],
        trend: 'Brand-safe response systems matter more than auto-posting volume.',
      },
      {
        id: 'menu-social',
        title: 'Menu updates and social content',
        impact: 'Keeps promotions moving without relying on last-minute manual effort.',
        tools: ['Canva', 'Buffer', 'Claude.ai'],
        trend: 'Operators are consolidating into fewer content tools with stronger scheduling and brand controls.',
      },
    ],
    marketWatch: [
      'Choose systems that connect reservations, messaging, and reviews before layering more AI tools.',
      'Most restaurants benefit more from reliable automations than high-end custom models.',
    ],
  },
  other: {
    summary: 'The strongest first use cases usually sit inside repetitive customer communication, internal coordination, or content operations.',
    workflows: [
      {
        id: 'customer-comms',
        title: 'Customer communication workflow',
        impact: 'Improves response time and consistency across common requests.',
        tools: ['Claude.ai', 'HubSpot', 'Zapier'],
        trend: 'Workflow reliability matters more than adding broad generic AI tools everywhere.',
      },
      {
        id: 'content-ops',
        title: 'Content and knowledge operations',
        impact: 'Turns repeated drafting work into structured templates and reusable assets.',
        tools: ['Notion', 'Claude.ai', 'Canva'],
        trend: 'Portable content systems age better than single-purpose generators.',
      },
      {
        id: 'handoffs',
        title: 'Internal handoffs and task routing',
        impact: 'Reduces dropped work between sales, service, and operations.',
        tools: ['Airtable', 'Make.com', 'Slack'],
        trend: 'Operational AI works best when ownership and routing are explicit.',
      },
    ],
    marketWatch: [
      'Scope the first pilot around one measurable workflow, not a company-wide AI mandate.',
      'Prefer tools that improve your current systems before buying a new isolated stack.',
    ],
  },
};

const formatMoney = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);

const roundHundreds = (value) => Math.round(value / 100) * 100;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const resolveInitialTab = () => {
  const hash = (window.location.hash || '').toLowerCase();
  const params = new URLSearchParams(window.location.search || '');
  const from = (params.get('from') || '').toLowerCase();

  if (hash === '#packages') {
    return 'packages';
  }
  if (hash === '#start' || hash === '#book' || from === 'finder') {
    return 'start';
  }
  if (hash === '#roi') {
    return 'roi';
  }
  if (hash === '#my-flow') {
    return 'my-flow';
  }
  return 'packages';
};

const getTabHash = (tabId) => {
  if (tabId === 'packages') return '#packages';
  if (tabId === 'start') return '#start';
  if (tabId === 'roi') return '#roi';
  if (tabId === 'my-flow') return '#my-flow';
  return '';
};

const inferAuthenticatedFromToken = () => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.localStorage.getItem('authToken'));
  } catch {
    return false;
  }
};

const isReadinessProfileComplete = (roi) => {
  const hasWorkflows = Array.isArray(roi.selectedWorkflows) && roi.selectedWorkflows.length > 0;
  const teamSize = Number(roi.teamSize);
  const systemsTouchCount = Number(roi.systemsTouchCount);
  const tools = Number(roi.toolCount);

  return Boolean(
    roi.businessType &&
    hasWorkflows &&
    teamSize >= 1 &&
    systemsTouchCount >= 1 &&
    roi.currentEcosystem &&
    tools >= 1 &&
    roi.currentToolStack &&
    roi.urgency &&
    roi.dataCentralization &&
    roi.compliance
  );
};

const getMissingReadinessFields = (roi) => {
  const missing = [];
  const teamSize = Number(roi.teamSize);
  const systemsTouchCount = Number(roi.systemsTouchCount);
  const tools = Number(roi.toolCount);

  if (!roi.businessType) missing.push('Business Type');
  if (!(teamSize >= 1)) missing.push('Employee Count');
  if (!roi.currentEcosystem) missing.push('Current Ecosystem');
  if (!(systemsTouchCount >= 1)) missing.push('Systems Touch Count');
  if (!(tools >= 1)) missing.push('Tools in Use');
  if (!String(roi.currentToolStack || '').trim()) missing.push('Current Tool Stack');
  if (!roi.dataCentralization) missing.push('Data Centralization');
  if (!roi.compliance) missing.push('Compliance Load');
  if (!roi.urgency) missing.push('Urgency');
  if (!Array.isArray(roi.selectedWorkflows) || roi.selectedWorkflows.length === 0) missing.push('At least one Workflow');

  return missing;
};

const getRequiredFieldContainerClass = (isMissing) =>
  `p-2 border transition-colors ${isMissing
    ? 'border-[rgba(255,232,0,0.45)] bg-[rgba(255,232,0,0.07)]'
    : 'border-transparent bg-transparent'}`;

const getRequiredFieldInputClass = (isMissing) =>
  `bg-[#050505] border text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none transition-colors ${isMissing
    ? 'border-[rgba(255,232,0.6)]'
    : 'border-[#2b2b2b]'}`;

const getInitialRoiState = () => {
  const defaultBand = getEmployeeBandMeta('2-5');
  const fallback = {
    employeeBand: defaultBand.value,
    teamSize: defaultBand.representative,
    businessType: '',
    currentEcosystem: 'microsoft',
    systemsTouchCount: 3,
    toolCount: '',
    currentToolStack: '',
    dataCentralization: inferDataCentralization(3),
    changeReadiness: 3,
    compliance: '',
    urgency: '',
    selectedWorkflows: [],
  };

  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const isAuthenticated = inferAuthenticatedFromToken();
    const parsed = loadReadinessProfile(isAuthenticated) || null;
    if (!parsed) {
      const context = loadBusinessContext(isAuthenticated);
      const contextBusinessType = normalizeIndustryKey(
        context.businessType || context.selectedStackId
      );
      if (!contextBusinessType) return fallback;
      return {
        ...fallback,
        businessType: contextBusinessType,
      };
    }
    const profile = parsed?.business_profile || {};
    const teamSize = Number(profile.team_size);
    const employeeBand = inferEmployeeBand(teamSize);
    const systemsTouchCount = Number(profile.systems_touch_count) || 3;
    const normalizedIndustry = normalizeIndustryKey(profile.business_type || profile.industry || '') || fallback.businessType;

    return {
      ...fallback,
      employeeBand,
      teamSize: Number.isFinite(teamSize) && teamSize > 0 ? teamSize : getEmployeeBandMeta(employeeBand).representative,
      businessType: normalizedIndustry,
      currentEcosystem: profile.current_ecosystem || fallback.currentEcosystem,
      systemsTouchCount,
      toolCount: profile.tool_count ?? '',
      currentToolStack: profile.current_tool_stack || '',
      dataCentralization: profile.data_centralization || inferDataCentralization(systemsTouchCount),
      changeReadiness: Number(profile.change_readiness) || 3,
      compliance: profile.compliance || '',
      urgency: profile.urgency || '',
      selectedWorkflows: Array.isArray(profile.selected_workflows) ? profile.selected_workflows : [],
    };
  } catch {
    return fallback;
  }
};

const mapTeamSizeToBizSize = (teamSizeRaw) => {
  const teamSize = Number(teamSizeRaw);
  if (!Number.isFinite(teamSize) || teamSize < 1) return '';
  if (teamSize <= 10) return 'small';
  if (teamSize <= 50) return 'medium';
  return 'large';
};

const normalizeApiErrorMessage = (value) => {
  if (!value) return 'Something went wrong. Please try again.';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && item.msg) return String(item.msg);
        return '';
      })
      .filter(Boolean)
      .join('; ');
    return joined || 'Something went wrong. Please try again.';
  }
  if (typeof value === 'object') {
    if (value.detail) return normalizeApiErrorMessage(value.detail);
    if (value.message) return String(value.message);
  }
  return String(value);
};

const toWeekdayIndex = (value = '') => {
  const key = String(value || '').trim().toLowerCase().replace(/\./g, '');
  return WEEKDAY_INDEX[key];
};

const parseTimeOfDay = (value = '') => {
  const normalized = String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();
  const parsed = parse(normalized, 'h:mm a', new Date());
  if (!Number.isFinite(parsed.getTime())) return null;
  return {
    hours: parsed.getHours(),
    minutes: parsed.getMinutes(),
  };
};

const toDateWithTime = (baseDate, timeOfDay) => {
  const next = new Date(baseDate);
  next.setHours(timeOfDay.hours, timeOfDay.minutes, 0, 0);
  return next;
};

const parseWeeklySlotRules = (rawSlots = []) => {
  const rules = [];
  const lines = Array.isArray(rawSlots) ? rawSlots : [];

  lines.forEach((rawLine) => {
    const line = String(rawLine || '').trim();
    if (!line || /^timezone\s*:/i.test(line)) return;

    const rangeMatch = line.match(/^([A-Za-z.]+)\s*-\s*([A-Za-z.]+)\s*,\s*([^-]+)\s*-\s*(.+)$/i);
    if (rangeMatch) {
      const startDay = toWeekdayIndex(rangeMatch[1]);
      const endDay = toWeekdayIndex(rangeMatch[2]);
      const startTime = parseTimeOfDay(rangeMatch[3]);
      const endTime = parseTimeOfDay(rangeMatch[4]);
      if (startDay != null && endDay != null && startTime && endTime) {
        rules.push({
          kind: 'window',
          days: [startDay, endDay],
          startTime,
          endTime,
        });
        return;
      }
    }

    const singleDayWindowMatch = line.match(/^([A-Za-z.]+)\s*,\s*([^-]+)\s*-\s*(.+)$/i);
    if (singleDayWindowMatch) {
      const dayIndex = toWeekdayIndex(singleDayWindowMatch[1]);
      const startTime = parseTimeOfDay(singleDayWindowMatch[2]);
      const endTime = parseTimeOfDay(singleDayWindowMatch[3]);
      if (dayIndex != null && startTime && endTime) {
        rules.push({
          kind: 'window',
          days: [dayIndex, dayIndex],
          startTime,
          endTime,
        });
        return;
      }
    }

    const singleSlotMatch = line.match(/^([A-Za-z.]+)\s+(.+)$/i);
    if (singleSlotMatch) {
      const dayIndex = toWeekdayIndex(singleSlotMatch[1]);
      const startTime = parseTimeOfDay(singleSlotMatch[2]);
      if (dayIndex != null && startTime) {
        rules.push({
          kind: 'single',
          day: dayIndex,
          startTime,
        });
      }
    }
  });

  return rules;
};

const isDayWithinRange = (day, startDay, endDay) => {
  if (startDay <= endDay) return day >= startDay && day <= endDay;
  return day >= startDay || day <= endDay;
};

const buildWeeklySlots = (rawSlots, weekStart) => {
  const rules = parseWeeklySlotRules(rawSlots);
  const byDay = Array.from({ length: 7 }, (_, index) => ({
    dayDate: addDays(weekStart, index),
    slots: [],
  }));
  const dedupe = new Set();

  rules.forEach((rule) => {
    if (rule.kind === 'single') {
      const dayBucket = byDay[rule.day];
      const start = toDateWithTime(dayBucket.dayDate, rule.startTime);
      const id = start.toISOString();
      if (!dedupe.has(id)) {
        dedupe.add(id);
        dayBucket.slots.push(start);
      }
      return;
    }

    const [startDay, endDay] = rule.days;
    byDay.forEach((bucket, dayIndex) => {
      if (!isDayWithinRange(dayIndex, startDay, endDay)) return;

      const windowStart = toDateWithTime(bucket.dayDate, rule.startTime);
      const windowEnd = toDateWithTime(bucket.dayDate, rule.endTime);
      if (windowEnd <= windowStart) return;

      let cursor = windowStart;
      while (addMinutes(cursor, SLOT_DURATION_MINUTES) <= windowEnd) {
        const id = cursor.toISOString();
        if (!dedupe.has(id)) {
          dedupe.add(id);
          bucket.slots.push(cursor);
        }
        cursor = addMinutes(cursor, SLOT_INTERVAL_MINUTES);
      }
    });
  });

  const now = new Date();
  return byDay.map((bucket) => ({
    ...bucket,
    slots: bucket.slots
      .filter((s) => s > now)
      .sort((a, b) => a.getTime() - b.getTime()),
  }));
};

const formatSelectedSlot = (date) => `${format(date, 'EEE MMM d, h:mm a')} (${SLOT_DURATION_MINUTES} min)`;

const ConsultingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const saveReadinessTimerRef = useRef(null);
  const syncOrchestratorTimerRef = useRef(null);
  const [activeTab, setActiveTab] = useState(resolveInitialTab());
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [readinessVisited, setReadinessVisited] = useState(false);
  const [readinessCompleted, setReadinessCompleted] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isReadinessCompletedStored(inferAuthenticatedFromToken());
  });
  const [form, setForm] = useState({
    bizName: '',
    contactName: '',
    email: '',
    phone: '',
    businessType: '',
    pains: [],
    bizSize: 'small',
    budget: 'unsure',
    currentTools: '',
    preferredSlot: '',
    goal: '',
  });
  const [startFieldOverrides, setStartFieldOverrides] = useState({
    businessType: false,
    bizSize: false,
    currentTools: false,
  });
  const [pulseCheckPayload, setPulseCheckPayload] = useState(null);
  const [startSyncMessage, setStartSyncMessage] = useState('');
  const [calendarSettings, setCalendarSettings] = useState({ timezone: '', booking_url: '', slots: CONSULTATION_WINDOWS });
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));

  const [roi, setRoi] = useState(getInitialRoiState);
  const industryOptions = useMemo(
    () => BUSINESS_TYPES,
    []
  );
  const lifecycleStatus = (!isAuthenticated || (user?.role || '').toLowerCase() !== 'user')
    ? 'guest'
    : (user?.newsletter_subscribed ? 'committed' : 'client_login');
  const readinessTenantId = useMemo(() => {
    if (!user?.id) return '';
    return `tenant-${user.id}`;
  }, [user?.id]);
  const readinessClientId = useMemo(() => {
    return user?.id ? String(user.id) : '';
  }, [user?.id]);
  const canAccessOrchestratorLab = lifecycleStatus !== 'guest' && Boolean(user?.id) && readinessCompleted;

  const readinessProfile = useMemo(() => PLAYBOOKS[resolvePlaybookKey(roi.businessType)] || PLAYBOOKS.other, [roi.businessType]);

  const personalizedPlaybook = useMemo(() => {
    const stackTokens = parseCurrentStackTokens(roi.currentToolStack);
    const ecosystemBias = ECOSYSTEM_PLAYBOOK_BIAS[roi.currentEcosystem] || {};

    const workflows = (readinessProfile.workflows || []).map((workflow) => {
      const tools = Array.isArray(workflow.tools) ? workflow.tools : [];
      const normalizedTools = tools.map((tool) => String(tool).toLowerCase());

      const stackMatches = tools.filter((tool) => {
        const normalized = String(tool).toLowerCase();
        return stackTokens.some((token) => normalized.includes(token) || token.includes(normalized));
      });

      const score = (stackMatches.length * 3) + (ecosystemBias[workflow.id] || 0);
      const prioritizedTools = [
        ...stackMatches,
        ...tools.filter((tool) => !stackMatches.includes(tool)),
      ].slice(0, 2);

      let fitReason = 'Balanced starting workflow for your current profile.';
      if (stackMatches.length > 0) {
        fitReason = `Aligned with your current stack: ${stackMatches.join(', ')}`;
      } else if (ecosystemBias[workflow.id]) {
        fitReason = `Prioritized for your ${roi.currentEcosystem === 'legacy' ? 'legacy/manual' : roi.currentEcosystem} ecosystem.`;
      }

      return {
        ...workflow,
        tools: prioritizedTools,
        _score: score,
        fitReason,
      };
    }).sort((a, b) => b._score - a._score);

    return {
      ...readinessProfile,
      workflows,
    };
  }, [readinessProfile, roi.currentToolStack, roi.currentEcosystem]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setRoi((prev) => {
      const validSelections = prev.selectedWorkflows.filter((workflowId) =>
        personalizedPlaybook.workflows.some((workflow) => workflow.id === workflowId)
      );
      const nextSelections = validSelections;

      if (
        nextSelections.length === prev.selectedWorkflows.length &&
        nextSelections.every((workflowId, index) => workflowId === prev.selectedWorkflows[index])
      ) {
        return prev;
      }

      return {
        ...prev,
        selectedWorkflows: nextSelections,
      };
    });
  }, [personalizedPlaybook]);

  const roiValues = useMemo(() => {
    const selectedWorkflowDefs = personalizedPlaybook.workflows.filter((workflow) => roi.selectedWorkflows.includes(workflow.id));
    const workflowCount = selectedWorkflowDefs.length || 1;
    const businessTypeKey = normalizeIndustryKey(roi.businessType) || 'legal';
    const businessTypeMeta = BUSINESS_TYPE_BENCHMARKS[businessTypeKey] || BUSINESS_TYPE_BENCHMARKS.legal;
    const employeeBandMeta = getEmployeeBandMeta(roi.employeeBand);
    const userCount = Number(roi.teamSize) > 0 ? Number(roi.teamSize) : employeeBandMeta.representative;
    const we = getEcosystemWeight(roi.currentEcosystem);
    const sm = employeeBandMeta.multiplier;
    const cs = getComplexityMultiplier(roi.systemsTouchCount);
    const systemsTouchCount = Number(roi.systemsTouchCount) || 1;

    const pilotFee = roundHundreds(ROI_CONSTANTS.basePilot * we * sm);
    const buildFee = roundHundreds((ROI_CONSTANTS.baseBuild * we * sm) + (cs * 5000));
    const annualReclaim = roundHundreds(userCount * ROI_CONSTANTS.hoursSavedPerUser * ROI_CONSTANTS.laborRate * 52);
    const coeLogic = roundHundreds((businessTypeMeta.baseError || 0) * cs);
    const annualOpportunity = annualReclaim + coeLogic;
    const monthlyInaction = Math.round(annualOpportunity / 12);
    const maintenance36Months = ROI_CONSTANTS.maintenanceMonthly * 36;
    const net3YearROI = roundHundreds((annualOpportunity * 3) - (buildFee + maintenance36Months));

    const complexityScore = clamp(Math.round(45 + (cs - 1) * 38), 35, 95);
    const automationPotential = clamp(Math.round(55 + (we - 1) * 20 + workflowCount * 4), 45, 98);
    const readinessScore = clamp(
      Math.round(
        40 +
        (Number(roi.changeReadiness) || 3) * 7 +
        ({ low: 8, medium: 3, high: -4 }[roi.compliance] || 0) +
        ({ exploring: -4, quarter: 2, now: 8 }[roi.urgency] || 0) -
        Math.max(systemsTouchCount - 5, 0)
      ),
      25,
      96
    );

    let recommendedPackageKey = 'assessment';
    if (userCount >= 25 || cs >= 1.7 || roi.compliance === 'high') {
      recommendedPackageKey = 'fullstack';
    } else if (userCount >= 6 || cs >= 1.3) {
      recommendedPackageKey = 'pilot';
    }

    const recommendedPackage = PACKAGES.find((pkg) => pkg.key === recommendedPackageKey) || PACKAGES[0];
    const minInvestment = Math.min(pilotFee, buildFee);
    const maxInvestment = Math.max(pilotFee, buildFee);
    const implementationCost = buildFee;
    const firstYearEstimatedCost = buildFee + (ROI_CONSTANTS.maintenanceMonthly * 12);
    const paybackMonths = firstYearEstimatedCost / (annualOpportunity / 12 || 1);
    const maturityBand = readinessScore < 55 ? 'Foundation stage' : readinessScore < 75 ? 'Pilot-ready' : 'Scale-ready';

    const principles = [
      'Revenue comfort threshold: firms between $1.5M and $3.0M CAD can usually absorb this as a strategic upgrade.',
      'Data sovereignty stance: Azure Canada East with human-in-the-loop approvals for legal and financial decisions.',
      systemsTouchCount >= 6
        ? 'You have a spaghetti-data profile (6+ systems touching the same record). Prioritize unification before adding more point tools.'
        : 'Your systems touch count is manageable enough for a fast 30-day pilot sprint.',
      'Delivery edge: compressing implementation cycles increases margin when pricing by reclaimed capacity and avoided errors.',
    ];

    return {
      selectedWorkflowDefs,
      workflowCount,
      businessTypeKey,
      businessTypeLabel: businessTypeMeta.label,
      workflowAnchor: businessTypeMeta.workflow,
      environmentWeight: we,
      sizeMultiplier: sm,
      complexityMultiplier: cs,
      systemsTouchCount,
      userCount,
      pilotFee,
      buildFee,
      annualReclaim,
      coeLogic,
      monthlyInaction,
      annualOpportunity,
      maintenance36Months,
      net3YearROI,
      annualManual: annualReclaim,
      annualSavings: annualOpportunity,
      implementationCost,
      firstYearEstimatedCost,
      paybackMonths,
      readinessScore,
      automationPotential,
      complexityScore,
      recommendedPackage,
      minInvestment,
      maxInvestment,
      maturityBand,
      principles,
    };
  }, [personalizedPlaybook, roi]);

  const intakePains = useMemo(() => {
    if (!form.businessType) return [];
    if (BUSINESS_TYPE_META[form.businessType]?.pains?.length) return BUSINESS_TYPE_META[form.businessType].pains;
    const benchmark = BUSINESS_TYPE_BENCHMARKS[normalizeIndustryKey(form.businessType)];
    if (!benchmark) return [];
    return [
      `${benchmark.workflow} is currently vulnerable to manual delay or errors.`,
      `Estimated annual COE baseline: ${formatMoney(benchmark.baseError)} before complexity multiplier.`,
      'Data handoffs across systems are increasing hidden rework and compliance risk.',
      'A 30-day pilot should prioritize measurable reclaim and controlled approvals.',
    ];
  }, [form.businessType]);

  const readinessFormComplete = useMemo(() => isReadinessProfileComplete(roi), [roi]);
  const readinessMissingFields = useMemo(() => getMissingReadinessFields(roi), [roi]);
  const missingReadinessFieldSet = useMemo(() => new Set(readinessMissingFields), [readinessMissingFields]);
  const weeklyCalendarSlots = useMemo(
    () => buildWeeklySlots(calendarSettings.slots || CONSULTATION_WINDOWS, calendarWeekStart),
    [calendarSettings.slots, calendarWeekStart]
  );
  const totalWeeklySlotCount = useMemo(
    () => weeklyCalendarSlots.reduce((count, day) => count + day.slots.length, 0),
    [weeklyCalendarSlots]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const draft = JSON.parse(window.sessionStorage.getItem(MY_AI_FLOW_DRAFT_KEY) || '{}');
      if (draft && typeof draft === 'object') {
        setForm((prev) => {
          const next = { ...prev };
          let changed = false;

          if (draft.clientName && !prev.bizName) {
            next.bizName = draft.clientName;
            changed = true;
          }
          if (draft.clientName && !prev.contactName) {
            next.contactName = draft.clientName;
            changed = true;
          }
          if (draft.contactEmail && !prev.email) {
            next.email = draft.contactEmail;
            changed = true;
          }
          const draftBusinessType = draft.businessType || '';
          if (draftBusinessType && prev.businessType !== draftBusinessType) {
            next.businessType = draftBusinessType;
            changed = true;
          }

          if (changed) {
            setStartSyncMessage('Get Started fields were updated from your latest My AI Flow and Readiness inputs.');
          }
          return changed ? next : prev;
        });
      }
    } catch {
      // Ignore malformed session payloads.
    }

    // Persist a lightweight draft so data isn't lost when navigating between tabs.
    const saveDraft = () => {
      try {
        window.sessionStorage.setItem(
          MY_AI_FLOW_DRAFT_KEY,
          JSON.stringify({
            clientName: form.bizName || form.contactName || '',
            contactEmail: form.email || '',
            businessType: form.businessType || '',
            preferredSlot: form.preferredSlot || '',
            phone: form.phone || '',
            updatedAt: new Date().toISOString(),
          })
        );
      } catch {
        // ignore storage errors
      }
    };

    const beforeUnload = () => saveDraft();
    window.addEventListener('beforeunload', beforeUnload);
    // also save immediately on mount to capture any default values
    saveDraft();

    consultingApi.getPublicCalendarSettings()
      .then((response) => {
        const data = response?.data?.settings || response?.data || {};
        setCalendarSettings({
          timezone: data.timezone || '',
          booking_url: data.booking_url || '',
          slots: Array.isArray(data.slots) && data.slots.length > 0 ? data.slots : CONSULTATION_WINDOWS,
        });
      })
      .catch(() => {
        setCalendarSettings((prev) => ({ ...prev, slots: prev.slots?.length ? prev.slots : CONSULTATION_WINDOWS }));
      });

    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  useEffect(() => {
    const readinessBizSize = mapTeamSizeToBizSize(roi.teamSize);
    const readinessBusinessType = roi.businessType || '';
    const readinessCurrentTools = (roi.currentToolStack || '').trim();

    setForm((prev) => {
      const next = { ...prev };
      let changedFromReadiness = false;

      if (!startFieldOverrides.businessType && readinessBusinessType && prev.businessType !== readinessBusinessType) {
        next.businessType = readinessBusinessType;
        next.pains = [];
        changedFromReadiness = true;
      }

      if (!startFieldOverrides.bizSize && readinessBizSize && prev.bizSize !== readinessBizSize) {
        next.bizSize = readinessBizSize;
        changedFromReadiness = true;
      }

      if (!startFieldOverrides.currentTools && readinessCurrentTools && prev.currentTools !== readinessCurrentTools) {
        next.currentTools = readinessCurrentTools;
        changedFromReadiness = true;
      }

      if (changedFromReadiness) {
        setStartSyncMessage('Get Started fields were refreshed from your latest Readiness inputs.');
        return next;
      }
      return prev;
    });
  }, [roi.businessType, roi.teamSize, roi.currentToolStack, startFieldOverrides]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Persist readiness state with the canonical business_type field so consulting and My AI Flow stay aligned.
    const readinessPayload = {
      completed: readinessFormComplete,
      timestamp: new Date().toISOString(),
      business_profile: {
        business_type: normalizeBusinessType(roi.businessType, ''),
        team_size: Number(roi.teamSize) || 0,
        employee_band: roi.employeeBand,
        current_ecosystem: roi.currentEcosystem,
        systems_touch_count: Number(roi.systemsTouchCount) || 0,
        tool_count: Number(roi.toolCount) || 0,
        current_tool_stack: roi.currentToolStack,
        data_centralization: roi.dataCentralization,
        change_readiness: Number(roi.changeReadiness) || 0,
        compliance: roi.compliance,
        urgency: roi.urgency,
        selected_workflows: roi.selectedWorkflows,
      },
      roi_summary: {
        readiness_score: roiValues.readinessScore,
        automation_potential: roiValues.automationPotential,
        complexity_score: roiValues.complexityScore,
        pilot_fee: roiValues.pilotFee,
        build_fee: roiValues.buildFee,
        annual_reclaim: roiValues.annualReclaim,
        cost_of_error: roiValues.coeLogic,
        monthly_inaction: roiValues.monthlyInaction,
        net_3yr_roi: roiValues.net3YearROI,
        payback_months: Number(roiValues.paybackMonths.toFixed(1)),
      },
    };

    saveReadinessProfile(readinessPayload, isAuthenticated);
    const normalizedIndustry = normalizeIndustryKey(roi.businessType);
    saveBusinessContext({
      businessType: normalizeBusinessType(normalizedIndustry, ''),
      selectedStackId: resolveStackIdFromBusinessType(normalizedIndustry, ''),
      currentToolStack: roi.currentToolStack,
      selectedWorkflows: roi.selectedWorkflows,
      teamSize: roi.teamSize,
      sourceScreen: 'readiness-studio',
    }, isAuthenticated);
    setReadinessCompleted(readinessFormComplete);

    if (isAuthenticated) {
      if (saveReadinessTimerRef.current) clearTimeout(saveReadinessTimerRef.current);
      saveReadinessTimerRef.current = setTimeout(() => {
        consultingApi.saveReadiness({
          business_profile: readinessPayload.business_profile,
          roi_summary: readinessPayload.roi_summary,
          completed: readinessFormComplete,
        }).catch(() => {});
      }, 1500);

      if (syncOrchestratorTimerRef.current) clearTimeout(syncOrchestratorTimerRef.current);
      syncOrchestratorTimerRef.current = setTimeout(async () => {
        if (!readinessTenantId || !readinessClientId) return;

        const selectedTools = String(roi.currentToolStack || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
          .map((toolName) => ({ tool_name: toolName, source: 'readiness_current_stack' }));

        const answers = [
          { question_key: 'team_size', response_value: Number(roi.teamSize) || 0 },
          { question_key: 'employee_band', response_value: roi.employeeBand || '' },
          { question_key: 'current_ecosystem', response_value: roi.currentEcosystem || '' },
          { question_key: 'systems_touch_count', response_value: Number(roi.systemsTouchCount) || 0 },
          { question_key: 'tool_count', response_value: Number(roi.toolCount) || 0 },
          { question_key: 'data_centralization', response_value: DATA_CENTRALIZATION_SCORE[roi.dataCentralization] || 0 },
          { question_key: 'change_readiness', response_value: Number(roi.changeReadiness) || 0 },
          { question_key: 'compliance_risk', response_value: COMPLIANCE_SCORE[roi.compliance] || 0 },
          { question_key: 'urgency_level', response_value: URGENCY_SCORE[roi.urgency] || 0 },
          { question_key: 'selected_workflow_count', response_value: Array.isArray(roi.selectedWorkflows) ? roi.selectedWorkflows.length : 0 },
        ];

        try {
          await Promise.all([
            orchestratorApi.upsertUserStack({
              tenant_id: readinessTenantId,
              client_id: readinessClientId,
              selected_tools: selectedTools,
              source_module: 'find_my_tools',
            }),
            orchestratorApi.upsertJourneyStage({
              tenant_id: readinessTenantId,
              client_id: readinessClientId,
              stage: readinessFormComplete ? 'readiness_audit' : 'tool_picking',
              metadata: {
                readiness_score: roiValues.readinessScore,
                automation_potential: roiValues.automationPotential,
                selected_workflows: roi.selectedWorkflows,
                selected_tool_count: selectedTools.length,
              },
            }),
          ]);

          if (readinessFormComplete) {
            await orchestratorApi.submitReadinessAudit({
              tenant_id: readinessTenantId,
              client_id: readinessClientId,
              answers,
              source: 'consulting_page',
            });
          }
        } catch {
          // Keep UX responsive when orchestrator service is unavailable.
        }
      }, 1800);
    }
  }, [
    roi,
    roiValues,
    readinessFormComplete,
    isAuthenticated,
    readinessTenantId,
    readinessClientId,
  ]);

  // Hydrate readiness from backend when the user is logged in
  useEffect(() => {
    if (!isAuthenticated) return;
    consultingApi.getReadiness().then((res) => {
      const data = res?.data;
      if (!data?.found) return;
      const bp = data.business_profile || {};
      const teamSize = Number(bp.team_size);
      const systemsTouchCount = Number(bp.systems_touch_count);
      const normalizedIndustry = normalizeIndustryKey(bp.business_type || '') || '';
      const inferredBand = inferEmployeeBand(teamSize);
      // Use > 0 guards for numeric fields so a backend 0 (saved when the field was
      // empty) never silently clobbers a value the user typed in the current session.
      setRoi((prev) => ({
        ...prev,
        businessType: normalizedIndustry || prev.businessType,
        employeeBand: bp.employee_band || inferredBand || prev.employeeBand,
        teamSize: (bp.team_size != null && Number(bp.team_size) > 0) ? bp.team_size : prev.teamSize,
        currentEcosystem: bp.current_ecosystem || prev.currentEcosystem,
        systemsTouchCount: Number.isFinite(systemsTouchCount) && systemsTouchCount > 0 ? systemsTouchCount : prev.systemsTouchCount,
        toolCount: (bp.tool_count != null && Number(bp.tool_count) > 0) ? bp.tool_count : prev.toolCount,
        currentToolStack: bp.current_tool_stack || prev.currentToolStack,
        dataCentralization: bp.data_centralization || inferDataCentralization(systemsTouchCount) || prev.dataCentralization,
        changeReadiness: Number(bp.change_readiness) || prev.changeReadiness,
        compliance: bp.compliance || prev.compliance,
        urgency: bp.urgency || prev.urgency,
        selectedWorkflows: Array.isArray(bp.selected_workflows) && bp.selected_workflows.length > 0 ? bp.selected_workflows : prev.selectedWorkflows,
      }));
      try {
        const existingBp = (loadReadinessProfile(true) || {}).business_profile || {};
        const safeBp = {
          business_type: normalizeIndustryKey(bp.business_type || '') || existingBp.business_type,
          team_size: bp.team_size > 0 ? bp.team_size : existingBp.team_size,
          employee_band: bp.employee_band || existingBp.employee_band,
          current_ecosystem: bp.current_ecosystem || existingBp.current_ecosystem,
          systems_touch_count: bp.systems_touch_count > 0 ? bp.systems_touch_count : existingBp.systems_touch_count,
          tool_count: bp.tool_count > 0 ? bp.tool_count : existingBp.tool_count,
          current_tool_stack: bp.current_tool_stack || existingBp.current_tool_stack,
          data_centralization: bp.data_centralization || existingBp.data_centralization,
          change_readiness: bp.change_readiness || existingBp.change_readiness,
          compliance: bp.compliance || existingBp.compliance,
          urgency: bp.urgency || existingBp.urgency,
          selected_workflows: (Array.isArray(bp.selected_workflows) && bp.selected_workflows.length > 0) ? bp.selected_workflows : existingBp.selected_workflows,
        };
        saveReadinessProfile({ found: true, completed: data.completed, business_profile: safeBp, roi_summary: data.roi_summary || {} }, true);
        const normalizedIndustryForContext = normalizeIndustryKey(safeBp.business_type || '');
        saveBusinessContext({
          businessType: normalizeBusinessType(normalizedIndustryForContext, ''),
          selectedStackId: resolveStackIdFromBusinessType(normalizedIndustryForContext, ''),
          currentToolStack: safeBp.current_tool_stack,
          selectedWorkflows: safeBp.selected_workflows,
          teamSize: safeBp.team_size,
          sourceScreen: 'readiness-studio',
        }, true);
      } catch {
        // Ignore local persistence merge issues and keep page interactive.
      }
    }).catch(() => {});
  }, [isAuthenticated]);

  const setTab = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'roi') {
      setReadinessVisited(true);
    }

    if (typeof window === 'undefined') {
      return;
    }

    const nextHash = getTabHash(tabId);
    const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
    window.history.replaceState(null, '', nextUrl);
  };

  useEffect(() => {
    const syncTabWithHash = () => {
      const hash = (window.location.hash || '').toLowerCase();
      if (hash === '#packages') {
        setActiveTab('packages');
      } else if (hash === '#start' || hash === '#book') {
        setActiveTab('start');
      } else if (hash === '#roi') {
        setActiveTab('roi');
        setReadinessVisited(true);
      } else if (hash === '#my-flow') {
        setActiveTab('my-flow');
      }
    };

    window.addEventListener('hashchange', syncTabWithHash);
    syncTabWithHash();
    return () => window.removeEventListener('hashchange', syncTabWithHash);
  }, [location.key]);

  useEffect(() => {
    if (activeTab === 'roi') {
      setReadinessVisited(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'my-flow' && canAccessOrchestratorLab) {
      navigate('/my-ai-flow');
    }
  }, [activeTab, canAccessOrchestratorLab, navigate]);

  const togglePain = (pain) => {
    setForm((prev) => ({
      ...prev,
      pains: prev.pains.includes(pain) ? prev.pains.filter((item) => item !== pain) : [...prev.pains, pain],
    }));
  };

  const queuePulseCheckForContact = () => {
    const payload = {
      business_type: roiValues.businessTypeKey,
      workflow_anchor: roiValues.workflowAnchor,
      user_count: roiValues.userCount,
      environment_weight: roiValues.environmentWeight,
      size_multiplier: roiValues.sizeMultiplier,
      complexity_multiplier: roiValues.complexityMultiplier,
      systems_touch_count: roiValues.systemsTouchCount,
      pilot_fee: roiValues.pilotFee,
      build_fee: roiValues.buildFee,
      annual_reclaim: roiValues.annualReclaim,
      cost_of_error: roiValues.coeLogic,
      monthly_inaction: roiValues.monthlyInaction,
      net_3yr_roi: roiValues.net3YearROI,
      data_residency: 'Azure Canada East',
      generated_at: new Date().toISOString(),
    };

    setPulseCheckPayload(payload);
    setForm((prev) => ({
      ...prev,
      businessType: prev.businessType || normalizeIndustryKey(roi.businessType),
      currentTools: prev.currentTools || roi.currentToolStack,
    }));
    setStartSyncMessage('Your Free Consultation ROI snapshot is queued for intake submission.');
    setTab('start');
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess(null);

    // Client-side validation: email format required
    if (!EMAIL_REGEX.test(String(form.email || '').trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    // If user selected a preferred slot (discovery call), phone number is required
    if (form.preferredSlot && !String(form.phone || '').trim()) {
      setError('Please provide a phone number when scheduling a discovery call.');
      return;
    }

    setLoading(true);

    try {
      const readinessCompleted = Boolean(form.bizName?.trim()) && readinessVisited;
      const readinessSnapshot = readinessCompleted
        ? {
                  readiness_score: roiValues.readinessScore,
            automation_potential: roiValues.automationPotential,
            complexity_score: roiValues.complexityScore,
            pilot_fee: roiValues.pilotFee,
            build_fee: roiValues.buildFee,
            annual_reclaim: roiValues.annualReclaim,
            cost_of_error: roiValues.coeLogic,
            monthly_inaction: roiValues.monthlyInaction,
            net_3yr_roi: roiValues.net3YearROI,
            payback_months: Number(roiValues.paybackMonths.toFixed(1)),
            selected_workflows: roiValues.selectedWorkflowDefs.map((workflow) => workflow.title),
            business_type: roiValues.businessTypeKey,
            urgency: roi.urgency,
            data_centralization: roi.dataCentralization,
          }
        : null;

      let myAiFlowPdfPackage = null;
      if (typeof window !== 'undefined') {
        try {
          myAiFlowPdfPackage = JSON.parse(window.sessionStorage.getItem(MY_AI_FLOW_PDF_PACKAGE_KEY) || 'null');
        } catch {
          myAiFlowPdfPackage = null;
        }
      }

      const payload = {
        biz_name: form.bizName,
        contact_name: form.contactName,
        email: form.email,
        phone: form.phone || null,
        business_type: form.businessType,
        biz_size: form.bizSize,
        pains: form.pains,
        budget: form.budget,
        preferred_slot: form.preferredSlot || null,
        current_tools: form.currentTools || null,
        goal: [
          form.goal || '',
          pulseCheckPayload ? `Free Consultation JSON:\n${JSON.stringify(pulseCheckPayload)}` : '',
        ].filter(Boolean).join('\n\n') || null,
        readiness_completed: readinessCompleted,
        readiness_snapshot: readinessSnapshot,
        my_ai_flow_pdf_package: myAiFlowPdfPackage,
      };

      const response = await consultingApi.submitIntake(payload);
      setSuccess({ clientName: form.bizName, clientId: response.data?.client_id });
    } catch (submitError) {
      setError(
        normalizeApiErrorMessage(
          submitError?.response?.data?.detail || submitError?.response?.data || submitError?.message
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white consulting-theme-shell">
      <Helmet>
        <title>AI Consulting for SMBs | Wowrolla</title>
        <meta
          name="description"
          content="Practical AI implementation for real estate, marketing, e-commerce, professional services, healthcare, and hospitality. Fixed-price packages from $2,500."
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-28 pb-14">
        <div className="mb-6">
          <p className="field-heading mb-3">Wowrolla Consulting</p>
          <h1 className="font-unbounded text-2xl md:text-3xl font-bold text-white">Practical AI for ANY team</h1>
          <p className="text-sm text-[#888] leading-relaxed mt-3">Choose your package, test operational readiness, and see which workflow sequence fits your business before you commit.</p>
          <div className="mt-4 inline-flex items-center gap-3 border border-[#1c1c1c] bg-[#0f0f0f] px-3 py-2">
            <span className="field-heading mb-0">Lifecycle status</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFE800]">
              {lifecycleStatus === 'guest' ? 'Guest' : lifecycleStatus === 'client_login' ? 'Client Login' : 'Committed User'}
            </span>
          </div>
        </div>

        <div className="sticky top-[64px] z-10 border-b border-[#1c1c1c] flex overflow-x-auto mb-6 consulting-tab-strip backdrop-blur-sm">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setTab(tab.id);
              }}
              className={`font-mono text-sm font-bold tracking-widest uppercase px-5 py-3 border-b-2 whitespace-nowrap ${
                activeTab === tab.id ? 'border-[#18de4d] text-[#f8e407]' : 'border-transparent text-[#9f9423]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'packages' && (
          <section className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {PACKAGES.map((pkg) => (
                <article key={pkg.key} className="bg-[#0f0f0f] border border-[#1c1c1c] p-4 rounded-none">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="field-heading mb-1">{pkg.phase}</p>
                      <h3 className="font-unbounded text-sm font-semibold text-white">{pkg.title}</h3>
                    </div>
                    {pkg.featured && (
                      <span className="font-mono text-[10px] tracking-widest uppercase px-2 py-1 border border-[#FF6B35] text-[#FF6B35]">Most Popular</span>
                    )}
                  </div>
                  <p className="font-unbounded font-bold mb-1" style={{ color: pkg.accent }}>{pkg.priceLabel}</p>
                  <p className="text-xs text-[#888] mb-3">{pkg.timeline}</p>
                  <div className="space-y-0.5 mb-3">
                    {pkg.deliverables.map((item) => (
                      <p key={item} className="text-xs text-[#888] leading-snug">- {item}</p>
                    ))}
                  </div>
                  <div className="space-y-0.5 mb-3">
                    <p className="text-[10px] text-white uppercase tracking-wider">Rate flexibility</p>
                    {pkg.scopeDrivers.map((driver) => (
                      <p key={driver} className="text-xs text-[#666] leading-snug">- {driver}</p>
                    ))}
                  </div>
                  <p className="text-xs text-[#888] leading-snug"><span className="text-white">Best for:</span> {pkg.bestFor}</p>
                </article>
              ))}
            </div>

            <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-5 rounded-none">
              <div className="flex items-start justify-between gap-4 mb-4 flex-col md:flex-row">
                <div>
                  <p className="field-heading mb-2">Interactive Scope Preview</p>
                  <h2 className="font-unbounded text-sm font-semibold text-white">Package fit should move with workflow complexity, not a static matrix</h2>
                  <p className="text-sm text-[#888] leading-relaxed mt-2 max-w-2xl">Estimate bands stay anchored to our package guide and flex by scope, integration load, compliance needs, and urgency.</p>
                </div>
                <button onClick={() => setTab('roi')} className="rounded-none font-mono text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors bg-[rgba(255,232,0,0.08)] border-[rgba(255,232,0,0.35)] text-[#FFE800] hover:bg-[rgba(255,232,0,0.14)]">
                  Open Readiness Studio -&gt;
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
                <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4">
                  <p className="field-heading mb-2">Current stage</p>
                  <p className="font-unbounded text-lg text-[#FFE800]">{roiValues.maturityBand}</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4">
                  <p className="field-heading mb-2">Recommended first engagement</p>
                  <p className="font-unbounded text-lg text-white">{roiValues.recommendedPackage.title}</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4">
                  <p className="field-heading mb-2">Likely investment</p>
                  <p className="font-unbounded text-lg text-[#39FF14]">{formatMoney(roiValues.minInvestment)} - {formatMoney(roiValues.maxInvestment)}</p>
                  <p className="text-xs text-[#666] mt-2">Final commercial quote is confirmed privately after scope validation.</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4">
                  <p className="field-heading mb-2">Payback outlook</p>
                  <p className="font-unbounded text-lg text-[#FF6B35]">{roiValues.paybackMonths.toFixed(1)} months</p>
                  <p className="text-xs text-[#666] mt-2">Modeled from your provided operations baseline.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4">
                  <p className="field-heading mb-3">Priority workflows in scope</p>
                  <div className="space-y-3">
                    {roiValues.selectedWorkflowDefs.map((workflow) => (
                      <div key={workflow.id}>
                        <p className="text-sm text-white">{workflow.title}</p>
                        <p className="text-xs text-[#888] mt-1">{workflow.tools.join(' -> ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4">
                  <p className="field-heading mb-3">Current implementation stance</p>
                  <div className="space-y-2">
                    {roiValues.principles.slice(0, 2).map((principle) => (
                      <p key={principle} className="text-sm text-[#888] leading-relaxed">- {principle}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'my-flow' && (
          <section className="bg-[#0f0f0f] border border-[#1c1c1c] p-5 rounded-none">
            <p className="field-heading mb-3">My AI Flow</p>
            {!readinessCompleted ? (
              <div className="bg-[#0a0a0a] border border-[rgba(255,232,0,0.35)] p-4 rounded-none">
                <p className="text-sm text-[#888]">
                  Start in Readiness Studio first. Complete all business profile fields, workflow priorities, and constraints so My AI Flow can auto-load your real inputs.
                </p>
                {readinessMissingFields.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-[#FFE800] uppercase tracking-widest mb-1">Fields left to complete</p>
                    <p className="text-sm text-[#aaa]">{readinessMissingFields.join(' | ')}</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setTab('roi')}
                  className="mt-3 text-sm px-3 py-2 border border-[#FFE800] text-[#FFE800]"
                >
                  Open Readiness Studio
                </button>
              </div>
            ) : canAccessOrchestratorLab ? (
              <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4 rounded-none">
                <h3 className="font-unbounded text-sm text-white">Run your guided AI flow simulation</h3>
                <p className="text-sm text-[#888] mt-2">
                  Redirecting you automatically to My AI Flow with your Readiness Studio values prefilled.
                </p>
                <p className="text-xs text-[#666] mt-2">If redirect does not start, use the direct link below.</p>
                <Link to="/my-ai-flow" className="inline-block mt-3 text-sm" style={{ color: '#FFE800' }}>
                  Open My AI Flow now -&gt;
                </Link>
              </div>
            ) : (
              <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4 rounded-none">
                <p className="text-sm text-[#888]">
                  Login with your client account to run My AI Flow.
                </p>
                <Link to={`/login?redirect=${encodeURIComponent('/consulting#my-flow')}`} className="inline-block mt-3 text-sm" style={{ color: '#FFE800' }}>
                  Client Login -&gt;
                </Link>
              </div>
            )}
          </section>
        )}

        {activeTab === 'start' && (
          <section id="start" data-booking-anchor="book" className="bg-[#0f0f0f] border border-[#1c1c1c] p-5 rounded-none">
            <div id="book" className="h-0 w-0 overflow-hidden" aria-hidden="true" />
            <p className="field-heading mb-3">Get Started Free</p>

            {lifecycleStatus === 'guest' && (
              <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4 mb-4 rounded-none">
                <p className="text-sm text-[#888]">
                  Guest mode: you can submit intake now, but client login unlocks orchestration-linked management after your discovery call.
                </p>
                <Link to={`/login?redirect=${encodeURIComponent('/consulting#start')}`} className="inline-block mt-3 text-sm" style={{ color: '#FFE800' }}>
                  Login as client -&gt;
                </Link>
              </div>
            )}

            {lifecycleStatus === 'committed' && (
              <div className="bg-[#0a0a0a] border border-[rgba(57,255,20,0.35)] p-4 mb-4 rounded-none">
                <p className="text-sm text-[#39FF14]">Committed user unlocked: free AI assessment-call promotion is active on your profile.</p>
              </div>
            )}

            <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4 mb-4 rounded-none">
              <p className="field-heading mb-2">{calendarSettings.title || 'Book A 20-Min Free Consultation'}</p>
              <h2 className="font-unbounded text-sm font-semibold text-white mb-2">Free strategy call before any paid package</h2>
              <p className="text-xs text-[#888] mb-3">Live weekly view. Each meeting is 20 minutes and every slot keeps a 20-minute buffer after it.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {calendarSettings.booking_url ? (
                  <a href={calendarSettings.booking_url} target="_blank" rel="noreferrer" className="text-xs px-3 py-1 border border-[#FFE800] text-[#FFE800]">Open booking calendar</a>
                ) : (
                  <a href="#book" className="text-xs px-3 py-1 border border-[#FFE800] text-[#FFE800]">Book free consultation</a>
                )}
                <button type="button" onClick={() => setTab('my-flow')} className="text-xs px-3 py-1 border border-[#1c1c1c] text-[#aaa] hover:text-white">
                  Open My AI Flow
                </button>
              </div>

              {success ? (
                <div className="border border-[#1c1c1c] bg-[#0a0a0a] p-4 text-center">
                  <p className="text-xs uppercase tracking-widest text-[#39FF14] mb-1">Slot Booked</p>
                  <p className="text-sm text-[#aaa]">Your appointment request has been received. This slot has been reserved — the calendar is now closed.</p>
                </div>
              ) : (
              <>
              <div className="border border-[#1c1c1c] bg-[#080808] p-3">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <p className="text-xs uppercase tracking-widest text-[#888]">Week of {format(calendarWeekStart, 'MMM d, yyyy')}</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCalendarWeekStart((prev) => addWeeks(prev, -1))}
                      className="text-xs px-2 py-1 border border-[#1c1c1c] text-[#aaa] hover:text-white"
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      onClick={() => setCalendarWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
                      className="text-xs px-2 py-1 border border-[#1c1c1c] text-[#aaa] hover:text-white"
                    >
                      This week
                    </button>
                    <button
                      type="button"
                      onClick={() => setCalendarWeekStart((prev) => addWeeks(prev, 1))}
                      className="text-xs px-2 py-1 border border-[#1c1c1c] text-[#aaa] hover:text-white"
                    >
                      Next
                    </button>
                  </div>
                </div>

                {totalWeeklySlotCount === 0 ? (
                  <p className="text-sm text-[#888]">No openings published in admin calendar for this week yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
                    {weeklyCalendarSlots.map((dayBucket) => (
                      <div key={dayBucket.dayDate.toISOString()} className="border border-[#1c1c1c] bg-[#0a0a0a] p-2">
                        <p className="text-xs uppercase tracking-wider text-[#888] mb-2">{format(dayBucket.dayDate, 'EEE d')}</p>
                        {dayBucket.slots.length === 0 ? (
                          <p className="text-xs text-[#666]">No slots</p>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {dayBucket.slots.map((slotDate) => {
                              const selectedValue = formatSelectedSlot(slotDate);
                              const isSelected = form.preferredSlot === selectedValue;
                              return (
                                <button
                                  key={slotDate.toISOString()}
                                  type="button"
                                  onClick={() => setForm((prev) => ({ ...prev, preferredSlot: selectedValue }))}
                                  className={`text-xs px-2 py-1 border ${isSelected ? 'border-[#FFE800] text-[#FFE800]' : 'border-[#1c1c1c] text-[#aaa] hover:text-white'}`}
                                >
                                  {format(slotDate, 'h:mm a')}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {form.preferredSlot && <p className="text-xs text-[#FFE800] mt-2">Selected slot: {form.preferredSlot}</p>}
              {calendarSettings.timezone && <p className="text-xs text-[#666] mt-2">Timezone: {calendarSettings.timezone}</p>}
              <p className="text-xs text-[#666] mt-3">Complete the intake below and we will send your calendar confirmation within 24 hours.</p>
              </>
            )}
            </div>

            <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-4 mb-4 rounded-none">
              <p className="field-heading mb-2">My AI Flow Access</p>
              {canAccessOrchestratorLab ? (
                <>
                  <p className="text-sm text-[#888]">Readiness profile complete and client login detected. My AI Flow opens directly with your saved business values.</p>
                  <Link to="/my-ai-flow" className="inline-block mt-3 text-sm" style={{ color: '#FFE800' }}>
                    Go to My AI Flow -&gt;
                  </Link>
                </>
              ) : (
                <div className="border border-[rgba(255,232,0,0.4)] bg-[rgba(255,232,0,0.05)] px-4 py-3 mt-1">
                  <p className="text-sm font-semibold text-[#FFE800]">&#9650; Complete AI Readiness Studio &amp; login with your client account to unlock My AI Flow.</p>
                  <p className="text-xs text-[#aaa] mt-1">Your readiness score and business profile are shared directly with your advisor before the call — giving you a faster, more tailored session.</p>
                </div>
              )}
            </div>

            {startSyncMessage && (
              <div className="bg-[rgba(255,232,0,0.08)] border border-[rgba(255,232,0,0.35)] p-3 mb-4">
                <p className="text-xs uppercase tracking-widest text-[#FFE800] mb-1">Auto Sync Active</p>
                <p className="text-sm text-[#ddd]">{startSyncMessage}</p>
              </div>
            )}

            {success ? (
              <div className="bg-[#0a0a0a] border border-[#1c1c1c] p-6 rounded-none">
                <h3 className="font-unbounded text-sm font-semibold text-[#39FF14] mb-2">Checkmark Confirmed</h3>
                <p className="text-sm text-white mb-1">Thanks, {success.clientName}.</p>
                <p className="text-sm text-[#888]">We&apos;ll reach out within 24 hours to schedule your free discovery call.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input required value={form.bizName} onChange={(e) => setForm((prev) => ({ ...prev, bizName: e.target.value }))} placeholder="Business Name" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                  <input required value={form.contactName} onChange={(e) => setForm((prev) => ({ ...prev, contactName: e.target.value }))} placeholder="Contact Name" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                  <input required type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} placeholder="Email" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                  <input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} placeholder="Phone (optional)" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                </div>

                <div>
                  <p className="field-heading mb-2">Business Type</p>
                  <select
                    value={form.businessType}
                    onChange={(e) => {
                      const value = e.target.value;
                      setStartFieldOverrides((prev) => ({ ...prev, businessType: true }));
                      setForm((prev) => ({ ...prev, businessType: value, pains: [] }));
                    }}
                    className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                  >
                    <option value="">Select a business type</option>
                    {industryOptions.map((option) => (
                      <option key={`${option.id}-${option.label}`} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {intakePains.length > 0 && (
                  <div>
                    <p className="field-heading mb-2">Pain Points</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {intakePains.map((pain) => (
                        <label key={pain} className="flex items-center gap-2 text-sm text-[#888]">
                          <input type="checkbox" checked={form.pains.includes(pain)} onChange={() => togglePain(pain)} className="accent-[#FFE800]" />
                          {pain}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="field-heading mb-2">Business Size</p>
                    <div className="grid grid-cols-3 gap-2">
                      {SIZE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setStartFieldOverrides((prev) => ({ ...prev, bizSize: true }));
                            setForm((prev) => ({ ...prev, bizSize: option.value }));
                          }}
                          className={`border px-3 py-2 text-sm rounded-none ${form.bizSize === option.value ? 'border-[#FFE800] text-[#FFE800]' : 'border-[#1c1c1c] text-[#888]'}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="field-heading mb-2">Budget Range</p>
                    <select value={form.budget} onChange={(e) => setForm((prev) => ({ ...prev, budget: e.target.value }))} className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none">
                      {BUDGET_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <p className="field-heading mb-2">Current Tools</p>
                    <input
                      value={form.currentTools}
                      onChange={(e) => {
                        setStartFieldOverrides((prev) => ({ ...prev, currentTools: true }));
                        setForm((prev) => ({ ...prev, currentTools: e.target.value }));
                      }}
                      placeholder="e.g. HubSpot, Shopify, QuickBooks..."
                      className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                    />
                  </div>
                </div>

                <div>
                  <p className="field-heading mb-2">Preferred Quick Slot</p>
                  <div className="bg-[#0a0a0a] border border-[#1c1c1c] text-sm px-3 py-2 text-[#ddd]">
                    {form.preferredSlot || 'Pick a slot from the weekly calendar above'}
                  </div>
                </div>

                <textarea rows={4} value={form.goal} onChange={(e) => setForm((prev) => ({ ...prev, goal: e.target.value }))} placeholder="90-day success goal" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />

                {pulseCheckPayload && (
                  <div className="bg-[rgba(0,212,255,0.08)] border border-[rgba(0,212,255,0.35)] p-3">
                    <p className="text-xs uppercase tracking-widest text-[#00D4FF] mb-1">Free Consultation Snapshot Ready</p>
                    <p className="text-xs text-[#aaa]">Your ROI snapshot JSON will be attached to this free consultation request automatically on submit.</p>
                  </div>
                )}

                {error && <p className="text-sm text-[#FF6B35]">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-none font-mono text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors bg-[rgba(255,232,0,0.08)] border-[rgba(255,232,0,0.35)] text-[#FFE800] hover:bg-[rgba(255,232,0,0.14)] disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Request Free Discovery Call ->'}
                </button>
              </form>
            )}
          </section>
        )}

        {activeTab === 'roi' && (
          <section>
            <div className="flex flex-col lg:flex-row gap-4 items-start">
              {/* LEFT: Readiness Studio Form */}
              <div className="flex-1 min-w-0 space-y-4">
                <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-5 rounded-none">
                  <div className="mb-4">
                    <p className="field-heading mb-2">Readiness Studio</p>
                    <h2 className="font-unbounded text-sm font-semibold text-white">Sales Bible ROI model with environment, size, complexity, and cost-of-error transparency</h2>
                    {readinessMissingFields.length > 0 && (
                      <div className="mt-3 p-3 border border-[rgba(255,232,0,0.5)] bg-[rgba(255,232,0,0.1)] shadow-[0_0_0_1px_rgba(255,232,0,0.2)]">
                        <p className="text-xs uppercase tracking-widest text-[#FFE800] mb-1">Complete required fields to unlock accurate ROI + My AI Flow</p>
                        <p className="text-sm text-white leading-relaxed">Focus these first: {readinessMissingFields.join(' | ')}</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className={`col-span-2 md:col-span-2 ${getRequiredFieldContainerClass(missingReadinessFieldSet.has('Business Type'))}`}>
                      <FieldHeader label="Business Type" hint={ROI_FIELD_HINTS.businessType} />
                      <select
                        value={roi.businessType}
                        onChange={(e) => {
                          const nextBusinessType = normalizeBusinessType(e.target.value) || e.target.value;
                          const hasMeaningfulSelections = Boolean(
                            roi.currentToolStack ||
                            roi.compliance ||
                            roi.urgency ||
                            (Array.isArray(roi.selectedWorkflows) && roi.selectedWorkflows.length > 0)
                          );

                          if (roi.businessType && roi.businessType !== nextBusinessType && hasMeaningfulSelections) {
                            const proceed = window.confirm('Changing business type will refresh ROI amounts and workflow recommendations, and selected workflows will reset. Continue?');
                            if (!proceed) return;
                          }

                          setRoi((prev) => ({
                            ...prev,
                            businessType: nextBusinessType,
                            selectedWorkflows: prev.businessType && prev.businessType !== nextBusinessType ? [] : prev.selectedWorkflows,
                          }));
                        }}
                        className={`${getRequiredFieldInputClass(missingReadinessFieldSet.has('Business Type'))} min-w-[280px]`}
                      >
                        {industryOptions.map((option) => (
                          <option key={`${option.id}-${option.label}`} value={option.id}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className={`col-span-2 md:col-span-2 ${getRequiredFieldContainerClass(missingReadinessFieldSet.has('Employee Count'))}`}>
                      <FieldHeader label="Employee Count" hint={ROI_FIELD_HINTS.teamSize} />
                      <input
                        type="range"
                        min="0"
                        max={EMPLOYEE_BANDS.length - 1}
                        step="1"
                        value={Math.max(EMPLOYEE_BANDS.findIndex((item) => item.value === roi.employeeBand), 0)}
                        onChange={(e) => {
                          const nextBand = EMPLOYEE_BANDS[Number(e.target.value)] || EMPLOYEE_BANDS[1];
                          setRoi((prev) => ({ ...prev, employeeBand: nextBand.value, teamSize: nextBand.representative }));
                        }}
                        className="w-full accent-[#FFE800]"
                      />
                      <p className="text-sm text-[#888] mt-1">
                        {getEmployeeBandMeta(roi.employeeBand).label} employees (modeled as {getEmployeeBandMeta(roi.employeeBand).representative})
                      </p>
                    </div>
                    <div className={`col-span-2 md:col-span-2 ${getRequiredFieldContainerClass(missingReadinessFieldSet.has('Current Ecosystem'))}`}>
                      <FieldHeader label="Current Ecosystem" hint={ROI_FIELD_HINTS.currentEcosystem} />
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {ECOSYSTEM_OPTIONS.map((option) => (
                          <label key={option.value} className={`border px-3 py-2 text-xs uppercase tracking-wider cursor-pointer ${roi.currentEcosystem === option.value ? 'border-[#FFE800] text-[#FFE800]' : missingReadinessFieldSet.has('Current Ecosystem') ? 'border-[rgba(255,232,0,0.6)] text-[#FFE800]' : 'border-[#2b2b2b] text-[#888]'}`}>
                            <input
                              type="radio"
                              name="ecosystem"
                              value={option.value}
                              checked={roi.currentEcosystem === option.value}
                              onChange={(e) => setRoi((prev) => ({ ...prev, currentEcosystem: e.target.value }))}
                              className="hidden"
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className={getRequiredFieldContainerClass(missingReadinessFieldSet.has('Systems Touch Count'))}>
                      <FieldHeader label="Systems Touch Count" hint={ROI_FIELD_HINTS.systemsTouchCount} />
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={roi.systemsTouchCount}
                        onChange={(e) => {
                          const next = Number(e.target.value) || 1;
                          setRoi((prev) => ({
                            ...prev,
                            systemsTouchCount: next,
                            dataCentralization: inferDataCentralization(next),
                          }));
                        }}
                        placeholder="How many apps touch one record"
                        className={getRequiredFieldInputClass(missingReadinessFieldSet.has('Systems Touch Count'))}
                      />
                    </div>
                    <div className={getRequiredFieldContainerClass(missingReadinessFieldSet.has('Data Centralization'))}>
                      <FieldHeader label="Data Centralization" hint={ROI_FIELD_HINTS.dataCentralization} />
                      <select value={roi.dataCentralization} onChange={(e) => setRoi((prev) => ({ ...prev, dataCentralization: e.target.value }))} className={getRequiredFieldInputClass(missingReadinessFieldSet.has('Data Centralization'))}>
                        <option value="">Select data centralization</option>
                        {DATA_CENTRALIZATION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className={`md:col-span-1 ${getRequiredFieldContainerClass(missingReadinessFieldSet.has('Tools in Use'))}`}>
                      <FieldHeader label="Tools in Use" hint={ROI_FIELD_HINTS.toolCount} />
                      <input type="number" min="1" max="50" value={roi.toolCount} onChange={(e) => setRoi((prev) => ({ ...prev, toolCount: e.target.value }))} placeholder="Current tool count" className={getRequiredFieldInputClass(missingReadinessFieldSet.has('Tools in Use'))} />
                    </div>
                    <div className={`md:col-span-2 ${getRequiredFieldContainerClass(missingReadinessFieldSet.has('Current Tool Stack'))}`}>
                      <FieldHeader label="Current Tool Stack" hint={ROI_FIELD_HINTS.currentToolStack} />
                      <input value={roi.currentToolStack} onChange={(e) => setRoi((prev) => ({ ...prev, currentToolStack: e.target.value }))} placeholder="HubSpot, Slack, Notion..." className={getRequiredFieldInputClass(missingReadinessFieldSet.has('Current Tool Stack'))} />
                    </div>
                    <div className={getRequiredFieldContainerClass(missingReadinessFieldSet.has('Compliance Load'))}>
                      <FieldHeader label="Compliance Load" hint={ROI_FIELD_HINTS.compliance} />
                      <select value={roi.compliance} onChange={(e) => setRoi((prev) => ({ ...prev, compliance: e.target.value }))} className={getRequiredFieldInputClass(missingReadinessFieldSet.has('Compliance Load'))}>
                        <option value="">Select compliance load</option>
                        {COMPLIANCE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className={getRequiredFieldContainerClass(missingReadinessFieldSet.has('Urgency'))}>
                      <FieldHeader label="Urgency" hint={ROI_FIELD_HINTS.urgency} />
                      <select value={roi.urgency} onChange={(e) => setRoi((prev) => ({ ...prev, urgency: e.target.value }))} className={getRequiredFieldInputClass(missingReadinessFieldSet.has('Urgency'))}>
                        <option value="">Select urgency</option>
                        {URGENCY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2 md:col-span-4">
                      <FieldHeader label="Change Readiness" hint={ROI_FIELD_HINTS.changeReadiness} />
                      <input type="range" min="1" max="5" step="1" value={roi.changeReadiness} onChange={(e) => setRoi((prev) => ({ ...prev, changeReadiness: Number(e.target.value) }))} className="w-full accent-[#FFE800]" />
                      <p className="text-sm text-[#888] mt-1">{CHANGE_READINESS_LABELS[roi.changeReadiness]}</p>
                    </div>
                  </div>

                    <div className={`bg-[#0a0a0a] border p-4 ${missingReadinessFieldSet.has('At least one Workflow') ? 'border-[rgba(255,232,0,0.45)]' : 'border-[#1c1c1c]'}`}>
                    <FieldHeader label="Business Type Workflow Playbook" hint={ROI_FIELD_HINTS.workflowPlaybook} />
                    <p className="text-xs text-[#888] leading-relaxed mb-2">{personalizedPlaybook.summary}</p>
                    <p className="text-xs text-[#00D4FF] leading-relaxed mb-3">Recommendations are re-ranked from your ecosystem and current stack, and synced to Readiness ROI planning.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {personalizedPlaybook.workflows.map((workflow) => {
                        const active = roi.selectedWorkflows.includes(workflow.id);
                        return (
                          <button
                            key={workflow.id}
                            type="button"
                            onClick={() => {
                              setRoi((prev) => {
                                const selected = prev.selectedWorkflows.includes(workflow.id)
                                  ? prev.selectedWorkflows.filter((workflowId) => workflowId !== workflow.id)
                                  : [...prev.selectedWorkflows, workflow.id];
                                return {
                                  ...prev,
                                  selectedWorkflows: selected.length ? selected : [workflow.id],
                                };
                              });
                            }}
                            className={`text-left border p-3 rounded-none transition-colors ${active ? 'border-[#FFE800] bg-[rgba(255,232,0,0.06)]' : 'border-[#1c1c1c] bg-[#080808]'}`}
                          >
                            <p className="field-heading mb-1">Workflow</p>
                            <p className="text-xs text-white mb-1">{workflow.title}</p>
                            <p className="text-xs text-[#888] leading-snug mb-2">{workflow.impact}</p>
                            <p className="text-[10px] text-[#c8c8c8] mb-1">{workflow.tools.join(' -> ')}</p>
                            <p className="text-[10px] text-[#00D4FF] leading-snug mb-1">{workflow.fitReason}</p>
                            <p className="text-[10px] text-[#666] leading-snug">{workflow.trend}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={queuePulseCheckForContact}
                    className="rounded-none font-mono text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors bg-[rgba(0,212,255,0.08)] border-[rgba(0,212,255,0.35)] text-[#00D4FF] hover:bg-[rgba(0,212,255,0.14)]"
                  >
                    Queue Free Consultation Snapshot -&gt;
                  </button>
                  <button onClick={() => setTab('start')} className="rounded-none font-mono text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors bg-[rgba(255,232,0,0.08)] border-[rgba(255,232,0,0.35)] text-[#FFE800] hover:bg-[rgba(255,232,0,0.14)]">
                    Book Free Discovery Call -&gt;
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab('my-flow')}
                    className="rounded-none font-mono text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors bg-[rgba(57,255,20,0.08)] border-[rgba(57,255,20,0.35)] text-[#39FF14] hover:bg-[rgba(57,255,20,0.14)]"
                  >
                    My AI Flow -&gt;
                  </button>
                </div>

                <p className="text-xs text-[#666]">This model uses We (ecosystem friction), Sm (employee scale), and Cs (systems touch complexity) to compute Pilot Fee, Build Fee, annual reclaim, COE, and net 3-year ROI. Values are planning estimates, not guaranteed outcomes.</p>
              </div>

              {/* RIGHT: Live Metrics Sidebar (sticky) */}
              <div className="lg:w-64 xl:w-72 space-y-2 lg:sticky lg:top-20 shrink-0">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-3 rounded-none">
                    <FieldHeader label="Readiness" />
                    <p className="font-unbounded font-bold text-[#FFE800] text-xl">{roiValues.readinessScore}<span className="text-sm font-normal text-[#888]">/100</span></p>
                  </div>
                  <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-3 rounded-none">
                    <FieldHeader label="Automation" />
                    <p className="font-unbounded font-bold text-[#39FF14] text-xl">{roiValues.automationPotential}<span className="text-sm font-normal text-[#888]">/100</span></p>
                  </div>
                  <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-3 rounded-none">
                    <FieldHeader label="Complexity" />
                    <p className="font-unbounded font-bold text-[#FF6B35] text-xl">{roiValues.complexityScore}<span className="text-sm font-normal text-[#888]">/100</span></p>
                  </div>
                  <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-3 rounded-none">
                    <FieldHeader label="We / Sm / Cs" />
                    <p className="font-unbounded font-bold text-[#00D4FF] text-base">{roiValues.environmentWeight.toFixed(1)} / {roiValues.sizeMultiplier.toFixed(1)} / {roiValues.complexityMultiplier.toFixed(1)}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-3 rounded-none">
                    <p className="field-heading mb-2">Pilot Sprint vs Enterprise Build</p>
                    <div className="space-y-2">
                      <div className="border border-[rgba(255,232,0,0.25)] p-2">
                        <p className="text-[10px] uppercase tracking-wider text-[#FFE800]">Pilot Sprint</p>
                        <p className="font-unbounded text-base text-white mt-1">{formatMoney(roiValues.pilotFee)}</p>
                      </div>
                      <div className="border border-[rgba(57,255,20,0.25)] p-2">
                        <p className="text-[10px] uppercase tracking-wider text-[#39FF14]">Enterprise Build</p>
                        <p className="font-unbounded text-base text-white mt-1">{formatMoney(roiValues.buildFee)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-3 rounded-none">
                    <p className="field-heading mb-1">Annual Capacity Reclaim (R)</p>
                    <p className="font-unbounded font-bold text-[#39FF14] text-base">{formatMoney(roiValues.annualReclaim)}</p>
                  </div>
                  <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-3 rounded-none">
                    <p className="field-heading mb-1">Cost of Error (COE)</p>
                    <p className="font-unbounded font-bold text-[#FF6B35] text-base">{formatMoney(roiValues.coeLogic)}</p>
                  </div>
                  <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-3 rounded-none">
                    <p className="field-heading mb-1">Cost of Inaction / Month</p>
                    <p className="font-unbounded font-bold text-[#FFE800] text-base">{formatMoney(roiValues.monthlyInaction)}</p>
                    <p className="text-[10px] text-[#666] mt-1">(R + COE) / 12</p>
                  </div>
                  <div className="bg-[#0f0f0f] border border-[#1c1c1c] p-3 rounded-none">
                    <p className="field-heading mb-1">Net 3-Year ROI</p>
                    <p className="font-unbounded font-bold text-white text-base">{formatMoney(roiValues.net3YearROI)}</p>
                    <p className="text-[10px] text-[#666] mt-1">((R + COE) x 3) - (Build + maintenance)</p>
                  </div>
                  <div className="bg-[#0f0f0f] border border-[rgba(255,107,53,0.35)] p-3 rounded-none">
                    <p className="text-xs uppercase tracking-widest text-[#FF6B35] mb-1">Cost of Inaction</p>
                    <p className="text-sm text-white">Every month of delay is modeled at {formatMoney(roiValues.monthlyInaction)} in missed reclaim + avoidable error cost.</p>
                  </div>
                  <div className="bg-[#0f0f0f] border border-[rgba(0,212,255,0.35)] p-3 rounded-none">
                    <p className="text-xs uppercase tracking-widest text-[#00D4FF]">Canadian Data Sovereignty: Azure Canada East</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ConsultingPage;


